import React, { useState } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { usePeriod } from "@/contexts/PeriodContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import DownloadLinkDialog from "@/components/DownloadLinkDialog";

export default function ExportPage() {
    const { activeTenant } = useTenant();
    const { zeitraum, setZeitraum } = usePeriod();
    const [busy, setBusy] = useState("");
    const [dlDialog, setDlDialog] = useState({ open: false, url: "", filename: "", title: "" });

    const downloadBlob = async (path, filename, title, key, extraParams = {}) => {
        if (!activeTenant) return;
        setBusy(key);
        try {
            const res = await api.get(path, {
                params: { tenant_id: activeTenant.tenant_id, ...extraParams },
                responseType: "blob",
            });
            const objUrl = URL.createObjectURL(res.data);
            setDlDialog({ open: true, url: objUrl, filename, title });
        } catch (e) {
            toast.error(`Export fehlgeschlagen: ${e?.response?.data?.detail || e.message || "Unbekannter Fehler"}`);
        } finally {
            setBusy("");
        }
    };

    return (
        <div className="space-y-6 pb-12 animate-fade-up">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Datenmanagement</div>
                <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-bold mt-1">Export</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Exportieren Sie Inventardaten von{" "}
                    <span className="text-tenant font-medium">{activeTenant?.name}</span> für Buchhaltung oder Archiv.
                </p>
            </div>

            {/* Bestandsveränderung (Multi-Jahres-Matrix) */}
            <Card className="p-6 bg-surface1 border-border max-w-3xl" data-testid="export-veraenderung-card">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    <FileText className="h-3.5 w-3.5" /> Buchhaltung
                </div>
                <h3 className="font-heading text-xl font-semibold mt-1">Bestandsveränderung (Mehrjahresübersicht)</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                    Pro Jahresübergang: Δ-Brutto, Δ-Wertberichtigung und Δ-Bilanzwert — getrennt nach Gesamt und Erlösgruppe (AB, KJ).
                    Layout entspricht der Excel-Vorlage und ist direkt an die Buchhaltung weiterleitbar.
                </p>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                    <button
                        type="button"
                        onClick={() => downloadBlob("/export/bestandsveraenderung.xlsx", `bestandsveraenderung_${activeTenant?.tenant_id}.xlsx`, "Bestandsveränderung als Excel herunterladen", "bv-xlsx")}
                        disabled={busy === "bv-xlsx" || !activeTenant}
                        data-testid="export-bv-xlsx-btn"
                        className="flex items-center justify-between p-4 border border-border rounded-md bg-surface2 hover:bg-surface1 text-left transition disabled:opacity-40"
                    >
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-md bg-emerald-500/15 flex items-center justify-center">
                                <FileSpreadsheet className="h-5 w-5 text-emerald-300" />
                            </div>
                            <div>
                                <div className="font-medium">Excel</div>
                                <div className="text-xs text-muted-foreground">Bearbeitbar · farbig · Σ-Zeile</div>
                            </div>
                        </div>
                        <Download className="h-4 w-4 text-muted-foreground" />
                    </button>
                    <button
                        type="button"
                        onClick={() => downloadBlob("/export/bestandsveraenderung.pdf", `bestandsveraenderung_${activeTenant?.tenant_id}.pdf`, "Bestandsveränderung als PDF herunterladen", "bv-pdf")}
                        disabled={busy === "bv-pdf" || !activeTenant}
                        data-testid="export-bv-pdf-btn"
                        className="flex items-center justify-between p-4 border border-border rounded-md bg-surface2 hover:bg-surface1 text-left transition disabled:opacity-40"
                    >
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-md bg-red-500/15 flex items-center justify-center">
                                <FileText className="h-5 w-5 text-red-300" />
                            </div>
                            <div>
                                <div className="font-medium">PDF</div>
                                <div className="text-xs text-muted-foreground">A4 quer · druckfertig</div>
                            </div>
                        </div>
                        <Download className="h-4 w-4 text-muted-foreground" />
                    </button>
                </div>
            </Card>

            {/* Inventur-Detail (für Steuerberater / Finanzamt) */}
            <Card className="p-6 bg-surface1 border-border max-w-3xl" data-testid="export-inventur-card">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    <FileText className="h-3.5 w-3.5" /> Steuerberater / Finanzamt
                </div>
                <h3 className="font-heading text-xl font-semibold mt-1">Inventur-Detail (vollständige Titelliste)</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                    Vollständige Liste aller aktiven Titel des gewählten Jahres mit Lager, Herstellkosten,
                    Lagerwert, Wertberichtigung, Bilanzwert und Tags (NE/ND/R/M).
                    <br />
                    <span className="text-foreground">Excel</span>: ein Tabellenblatt pro Erlösgruppe (AB, KJ …) mit Σ-Zeile · <span className="text-foreground">PDF</span>: A4 quer, eine Seite pro Erlösgruppe.
                </p>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <Tabs value={zeitraum} onValueChange={setZeitraum}>
                        <TabsList data-testid="export-inventur-zeitraum-tabs">
                            {["2023", "2024", "2025", "2026"].map((y) => (
                                <TabsTrigger key={y} value={y}>
                                    {y}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                </div>

                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                    <button
                        type="button"
                        onClick={() => downloadBlob(`/export/inventur.xlsx`, `inventur_detail_${activeTenant?.tenant_id}_${zeitraum}.xlsx`, `Inventur-Detail ${zeitraum} als Excel herunterladen`, "inv-xlsx", { zeitraum })}
                        disabled={busy === "inv-xlsx" || !activeTenant}
                        data-testid="export-inv-xlsx-btn"
                        className="flex items-center justify-between p-4 border border-border rounded-md bg-surface2 hover:bg-surface1 text-left transition disabled:opacity-40"
                    >
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-md bg-emerald-500/15 flex items-center justify-center">
                                <FileSpreadsheet className="h-5 w-5 text-emerald-300" />
                            </div>
                            <div>
                                <div className="font-medium">Excel · Jahr {zeitraum}</div>
                                <div className="text-xs text-muted-foreground">Pro Erlösgruppe ein Blatt mit Σ-Zeile</div>
                            </div>
                        </div>
                        <Download className="h-4 w-4 text-muted-foreground" />
                    </button>
                    <button
                        type="button"
                        onClick={() => downloadBlob(`/export/inventur.pdf`, `inventur_detail_${activeTenant?.tenant_id}_${zeitraum}.pdf`, `Inventur-Detail ${zeitraum} als PDF herunterladen`, "inv-pdf", { zeitraum })}
                        disabled={busy === "inv-pdf" || !activeTenant}
                        data-testid="export-inv-pdf-btn"
                        className="flex items-center justify-between p-4 border border-border rounded-md bg-surface2 hover:bg-surface1 text-left transition disabled:opacity-40"
                    >
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-md bg-red-500/15 flex items-center justify-center">
                                <FileText className="h-5 w-5 text-red-300" />
                            </div>
                            <div>
                                <div className="font-medium">PDF · Jahr {zeitraum}</div>
                                <div className="text-xs text-muted-foreground">A4 quer · pro Erlösgruppe eine Seite</div>
                            </div>
                        </div>
                        <Download className="h-4 w-4 text-muted-foreground" />
                    </button>
                </div>
            </Card>

            <DownloadLinkDialog
                open={dlDialog.open}
                onOpenChange={(o) => setDlDialog((d) => ({ ...d, open: o }))}
                url={dlDialog.url}
                title={dlDialog.title || "Download"}
                description="Der Download wird in einem neuen Tab gestartet. Falls Ihr Browser den neuen Tab blockiert, kopieren Sie die URL und öffnen Sie sie manuell."
                filename={dlDialog.filename}
                expiresInSec={600}
            />
        </div>
    );
}
